import * as React from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Grid, IconButton, Button, Box, Typography,
  Paper, Divider, createStyles, makeStyles,
  Collapse,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { useSnackbar } from 'notistack';
import CompanyFilter from './RuleFilters/CompanyFilter';
import type { ICompany } from '../../massReceive/MassReceiveInterfaces';
import type {
  IMarkingDynamicBehavior, IRuleCondition, IRuleOutcome, RuleFiltersModes,
} from './Utilities/PolicyConfiguratorInterfaces';
import {
  companyRequiredWorkflowTypes, POLICYCONFIGURATORSECTIONS,
} from './Utilities/PolicyConfiguratorInterfaces';
import ConditionFilter from './RuleFilters/ConditionFilter';
import OutcomeFilter from './RuleFilters/OutcomeFilter';
import MarkingDynamicBehaviorFilter from './RuleFilters/MarkingDynamicBehaviorFilter';

const useStyles = makeStyles(() => createStyles({
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
}));

interface IRuleFilterProps {
  workflowName: string;
  workflowType: POLICYCONFIGURATORSECTIONS;
  filterMode: RuleFiltersModes;
  conditionFilterValues: IRuleCondition[];
  setConditionFilterValues: (values: IRuleCondition[]) => void;
  outcomeFilterValues: IRuleOutcome[];
  setOutcomeFilterValues: (values: IRuleOutcome[]) => void;
  companyFilterValue?: ICompany;
  setCompanyFilterValue: (values: ICompany) => void;
  submitFilterValues: () => void;
  showErrors: boolean;
  setShowErrors?: (value: boolean) => void;
  companyRef?: React.RefObject<HTMLInputElement>;
  handleClearAll: () => void;
  markingDynamicBehavior?: IMarkingDynamicBehavior | null;
  setMarkingDBFormValues?: (values: IMarkingDynamicBehavior) => void;
  isMarkingDynamicBehavior?: boolean;
  setIsMarkingDynamicBehavior?: (value: boolean) => void;
  isQuantity?: boolean;
  setIsQuantity?: (value: boolean) => void;
}

const RuleFilter: React.FC<IRuleFilterProps> = ({
  workflowName,
  workflowType,
  filterMode,
  conditionFilterValues, setConditionFilterValues,
  outcomeFilterValues, setOutcomeFilterValues,
  companyFilterValue, setCompanyFilterValue,
  submitFilterValues,
  showErrors, setShowErrors,
  companyRef,
  handleClearAll,
  markingDynamicBehavior, setMarkingDBFormValues,
  isMarkingDynamicBehavior, setIsMarkingDynamicBehavior,
  isQuantity, setIsQuantity,
}) => {
  // Initial imports
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // State hooks for dynamic component elements
  const handleAddNewCondition = () => {
    if (conditionFilterValues.some((condition) => condition.field === '' || condition.operation === '' || condition.value === '')) {
      if (setShowErrors !== undefined) { setShowErrors(true); }
      enqueueSnackbar('Cannot add new condition, while previous conditions are not filled out', { variant: 'error' });
    } else {
      const temp = [...conditionFilterValues];
      temp.push({
        field: '',
        operation: '',
        value: '',
      });
      setConditionFilterValues([...temp]);
    }
  };

  const handleRemoveCondition = (index: number) => {
    if (conditionFilterValues.length > 1) {
      const temp = [...conditionFilterValues];
      temp.splice(index, 1);
      setConditionFilterValues([...temp]);
    } else {
      enqueueSnackbar('Need at least one condition to create a rule.', { variant: 'error' });
    }
  };

  const handleUpdateCondition = (indexParam: number, conditionParam: IRuleCondition) => {
    const tempConditions = [...conditionFilterValues];
    tempConditions[indexParam] = conditionParam;
    setConditionFilterValues([...tempConditions]);
  };

  const handleUpdateOutcome = (indexParam: number, outcomeParam: IRuleOutcome) => {
    const tempOutcomes = [...outcomeFilterValues];
    tempOutcomes[indexParam] = outcomeParam;
    setOutcomeFilterValues([...tempOutcomes]);
    setIsMarkingDynamicBehavior?.(outcomeParam.value === 'Manager Notification');
  };

  React.useEffect(() => {
    if (window.location.pathname.toLowerCase().includes('edit-markingdynamicbehavior')) {
      setIsMarkingDynamicBehavior?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Component configuration based on props
  const companyRequired = (workflowName || '') === '' ? true : companyRequiredWorkflowTypes.includes(workflowName);

  // Render
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Collapse in={companyRequired}>
            <Box py={3}>
              <Paper>
                <Grid item xs={12}>
                  <Box pl={2} py={2}>
                    <Typography className={classes.title}>{'Select Company'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Box pl={2} py={2}>
                  <CompanyFilter
                    companyFormValue={companyFilterValue}
                    setCompanyFormValue={setCompanyFilterValue}
                    companyRef={companyRef}
                  />
                </Box>
              </Paper>
            </Box>
            {companyFilterValue && (
              <Box py={1}>
                <Alert severity="info">{'Refine your results with the Conditions and Outcome filters'}</Alert>
              </Box>
            )}
          </Collapse>
          <>
            <Box py={3}>
              <Paper>
                <Grid item xs={12}>
                  <Box pl={2} py={2}>
                    <Typography className={classes.title}>
                      {`${filterMode} Conditions`}
                    </Typography>
                    { filterMode !== 'Filter' && ['Labor Code', 'Part Code'].includes(workflowName) && (
                      <FormControlLabel
                        style={{ pointerEvents: 'none' }}
                        control={(
                          <Checkbox
                            id="isQuantity-checkbox"
                            style={{ pointerEvents: 'auto' }}
                            color="primary"
                            name="isQuantity"
                            checked={isQuantity}
                            onChange={() => {
                              setIsQuantity?.(!isQuantity);
                            }}
                          />
                        )}
                        label="Quantity Rule Calculation"
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Box p={2}>
                    {conditionFilterValues.map((condition, idx) => (
                      <ConditionFilter
                        index={idx}
                        condition={condition}
                        updateCondition={handleUpdateCondition}
                        handleRemoveCondition={handleRemoveCondition}
                        showCollectionOperators={workflowType === POLICYCONFIGURATORSECTIONS.ROUTING}
                        showErrors={showErrors}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box px={1} clone>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => { handleAddNewCondition(); }}
                      style={{ minWidth: '5.625rem' }}
                    >
                      <IconButton disableFocusRipple disabled disableRipple>
                        <AddCircleIcon style={{ color: '#6CAE75', float: 'right' }} fontSize="large" />
                      </IconButton>
                      {'ADD CONDITION'}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box pl={2} py={2}>
                    <Typography className={classes.title}>
                      {`${filterMode} Outcome`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Box pl={2} py={2}>
                    {outcomeFilterValues.map((outcome, idx) => (
                      <OutcomeFilter
                        index={idx}
                        workflowType={workflowType}
                        workflowName={workflowName}
                        filterMode={filterMode}
                        outcome={outcome}
                        updateOutcome={handleUpdateOutcome}
                        showErrors={showErrors}
                      />
                    ))}
                    {
                      isMarkingDynamicBehavior
                      && (
                      <MarkingDynamicBehaviorFilter
                        markingDynamicBehavior={markingDynamicBehavior}
                        setMarkingDBFormValues={setMarkingDBFormValues}
                      />
                      )
                    }
                  </Box>
                </Grid>
              </Paper>
            </Box>
            <Box py={1}>
              <Grid item xs={12} container spacing={2} direction="row-reverse">
                <Box pt={5}>
                  <Button color="primary" variant="contained" onClick={submitFilterValues}>{`${filterMode}`}</Button>
                </Box>
                <Box pt={5} mr={2}>
                  <Button color="secondary" variant="text" onClick={handleClearAll}>{'CLEAR ALL'}</Button>
                </Box>
              </Grid>
            </Box>
          </>
        </Grid>
      </Grid>
    </>
  );
};

export default RuleFilter;
